import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import Header from '../../components/layout/Header';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await apiRequest('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // If request is successful, show success message
  if (success) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background">
        <div className="flex h-full grow flex-col">
          <Header variant="auth" />

          <div className="px-40 flex flex-1 justify-center py-5">
            <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
              <div className="text-center py-8">
                <div className="mb-6">
                  <svg className="w-20 h-20 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                  Check your email
                </h2>
                <p className="text-text-secondary text-base px-4 pb-6">
                  If an account exists with <strong className="text-white">{email}</strong>, we've sent password reset instructions.
                </p>
                <p className="text-text-secondary text-sm px-4 pb-8">
                  The link will expire in 15 minutes for security reasons.
                </p>
                <p className="text-text-secondary text-xs px-4 pb-6">
                  Didn't receive the email? Check your spam folder or wait a few minutes.
                </p>
                <Link
                  to="/login"
                  className="inline-block px-6 py-2 bg-primary text-white rounded font-medium hover:bg-primary/90 transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div className="flex h-full grow flex-col">
        <Header variant="auth" />

        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-center pb-3 pt-5">
              Reset your password
            </h2>
            <p className="text-text-secondary text-base px-4 text-center pb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            {error && (
              <div className="mx-4 mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col items-center w-full">
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Email</p>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] text-base font-normal leading-normal"
                  />
                </label>
              </div>
              <div className="flex px-4 py-3 w-full justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex max-w-[450px] w-full cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-5 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="truncate">
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </span>
                </button>
              </div>
              <div className="flex px-4 py-3 w-full justify-center">
                <Link
                  to="/login"
                  className="text-primary hover:text-primary/80 text-sm transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
